/**
 * FIVL global module.
 * Small utilities shared by every component, plus the elements that are needed on every page.
 * No frameworks. Everything else loads per section as an ES module.
 */

export const debounce = (fn, wait = 200) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

export const fetchConfig = (type = 'json') => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
});

export const formatMoney = (cents, format = window.FIVL?.moneyFormat || '{{amount}}') => {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholderRegex);
  const key = match ? match[1] : 'amount';
  const value = Number(cents) / 100;
  const withDelimiters = (number, precision, thousands, decimal) => {
    const fixed = number.toFixed(precision);
    const [int, dec] = fixed.split('.');
    const intWithThousands = int.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return dec ? `${intWithThousands}${decimal}${dec}` : intWithThousands;
  };
  let formatted;
  switch (key) {
    case 'amount_no_decimals':
      formatted = withDelimiters(value, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      formatted = withDelimiters(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      formatted = withDelimiters(value, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      formatted = withDelimiters(value, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      formatted = withDelimiters(value, 0, ' ', ',');
      break;
    default:
      formatted = withDelimiters(value, 2, ',', '.');
  }
  return format.replace(placeholderRegex, formatted);
};

/* ---------- Tiny pub/sub for cart and product events ---------- */
const subscribers = {};
export const subscribe = (eventName, callback) => {
  (subscribers[eventName] ||= []).push(callback);
  return () => {
    subscribers[eventName] = subscribers[eventName].filter((cb) => cb !== callback);
  };
};
export const publish = (eventName, data) => {
  (subscribers[eventName] || []).forEach((cb) => cb(data));
};
export const EVENTS = {
  cartUpdate: 'cart:update',
  cartError: 'cart:error',
  variantChange: 'variant:change',
  quantityUpdate: 'quantity:update',
};

/* ---------- Focus trap ---------- */
const FOCUSABLE = 'summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';
const trapHandlers = {};

export const trapFocus = (container, elementToFocus = container) => {
  const elements = Array.from(container.querySelectorAll(FOCUSABLE));
  const first = elements[0];
  const last = elements[elements.length - 1];
  removeTrapFocus();

  trapHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapHandlers.keydown);
  };
  trapHandlers.focusout = () => document.removeEventListener('keydown', trapHandlers.keydown);
  trapHandlers.keydown = (event) => {
    if (event.code !== 'Tab') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapHandlers.focusout);
  document.addEventListener('focusin', trapHandlers.focusin);
  elementToFocus.focus();
  if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
};

export const removeTrapFocus = (elementToFocus = null) => {
  document.removeEventListener('focusin', trapHandlers.focusin);
  document.removeEventListener('focusout', trapHandlers.focusout);
  document.removeEventListener('keydown', trapHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
};

/* ---------- Dialog / drawer base element ---------- */
export class ThemeDialog extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('dialog');
    this.openers = document.querySelectorAll(`[data-dialog-open="${this.id}"]`);
  }

  connectedCallback() {
    if (!this.dialog) return;
    this.dialog.setAttribute('scroll-lock', '');
    this.openers.forEach((opener) => {
      opener.setAttribute('aria-haspopup', 'dialog');
      opener.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(opener);
      });
    });
    // Delegated so re-rendered content (cart drawer, quick add) keeps working.
    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-dialog-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => this.onClose());
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.close();
    });
  }

  open(opener) {
    this.opener = opener;
    this.dialog.showModal();
    this.classList.add('is-open');
    const focusTarget = this.dialog.querySelector('[autofocus]') || this.dialog.querySelector(FOCUSABLE) || this.dialog;
    trapFocus(this.dialog, focusTarget);
    this.dispatchEvent(new CustomEvent('dialog:open'));
  }

  close() {
    this.classList.remove('is-open');
    const done = () => this.dialog.close();
    if (this.hasAttribute('data-animated')) {
      this.dialog.addEventListener('animationend', done, { once: true });
      this.dialog.classList.add('is-closing');
      setTimeout(done, 300);
    } else {
      done();
    }
  }

  onClose() {
    this.dialog.classList.remove('is-closing');
    removeTrapFocus(this.opener);
    this.dispatchEvent(new CustomEvent('dialog:close'));
  }
}
if (!customElements.get('theme-dialog')) customElements.define('theme-dialog', ThemeDialog);

/* ---------- Reveal on scroll ---------- */
const STAGGER_STEP = 70;
const STAGGER_MAX = 8;
let revealObserver;

const revealTargets = (root = document) => {
  const singles = Array.from(root.querySelectorAll('[data-reveal]:not(.is-revealed):not([data-reveal-observed])'));
  const groups = document.body.classList.contains('animations-stagger')
    ? Array.from(root.querySelectorAll('[data-reveal-stagger]:not([data-reveal-observed])'))
    : [];
  return { singles, groups };
};

const revealNow = (element) => {
  if (element.hasAttribute('data-reveal-stagger')) {
    Array.from(element.children).forEach((child, index) => {
      child.style.setProperty('--reveal-delay', `${Math.min(index, STAGGER_MAX) * STAGGER_STEP}ms`);
      child.classList.add('is-revealed');
    });
  } else {
    element.classList.add('is-revealed');
  }
};

export const initReveal = (root = document) => {
  if (!document.body.classList.contains('reveal-enabled') || document.documentElement.classList.contains('shopify-design-mode')) return;
  const { singles, groups } = revealTargets(root);
  const targets = [...singles, ...groups];
  if (!targets.length) return;
  if (!('IntersectionObserver' in window)) {
    targets.forEach(revealNow);
    return;
  }
  revealObserver ||= new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealNow(entry.target);
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0 }
  );
  targets.forEach((el) => {
    el.setAttribute('data-reveal-observed', '');
    // Anything already above the fold on load should not wait.
    const rect = el.getBoundingClientRect();
    if (rect.bottom < 0) {
      revealNow(el);
      return;
    }
    revealObserver.observe(el);
  });
};
initReveal();
document.addEventListener('shopify:section:load', () => initReveal());
document.addEventListener('fivl:content', (event) => initReveal(event.target instanceof Element ? event.target : document));
// Stagger groups that are not observed for whatever reason must never stay invisible.
setTimeout(() => {
  document.querySelectorAll('[data-reveal-stagger]:not([data-reveal-observed])').forEach(revealNow);
}, 2500);

/* ---------- Animated counters ---------- */
const animateCounter = (el) => {
  const text = el.textContent.trim();
  const match = text.match(/^([^0-9]*)([0-9][0-9.,]*)(.*)$/);
  if (!match) return;
  const [, prefix, number, suffix] = match;
  const decimals = (number.split('.')[1] || '').length;
  const target = parseFloat(number.replace(/,/g, ''));
  if (Number.isNaN(target)) return;
  const duration = 1200;
  const start = performance.now();
  const step = (now) => {
    const progress = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    el.textContent = `${prefix}${(target * eased).toFixed(decimals)}${suffix}`;
    if (progress < 1) requestAnimationFrame(step);
    else el.textContent = text;
  };
  requestAnimationFrame(step);
};
const initCounters = () => {
  if (!document.body.classList.contains('animations-counters') || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const counters = document.querySelectorAll('[data-counter]:not([data-counter-done])');
  if (!counters.length || !('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.setAttribute('data-counter-done', '');
      animateCounter(entry.target);
      observer.unobserve(entry.target);
    });
  });
  counters.forEach((el) => observer.observe(el));
};
initCounters();
document.addEventListener('shopify:section:load', initCounters);

/* ---------- Back to top ---------- */
const backToTop = document.querySelector('[data-back-to-top]');
if (backToTop) {
  const toggle = () => (backToTop.hidden = window.scrollY < window.innerHeight * 1.5);
  window.addEventListener('scroll', toggle, { passive: true });
  toggle();
  backToTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
}

/* ---------- Cart count bubble ---------- */
subscribe(EVENTS.cartUpdate, ({ cart }) => {
  if (!cart) return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = cart.item_count;
    el.toggleAttribute('hidden', cart.item_count === 0);
  });
});

/* ---------- Cart helpers ---------- */
export const cartRequest = async (url, body) => {
  const response = await fetch(url, { ...fetchConfig(), body: JSON.stringify(body) });
  const data = await response.json();
  if (!response.ok) {
    publish(EVENTS.cartError, data);
    throw data;
  }
  return data;
};

export const refreshCart = async () => {
  const response = await fetch(`${window.FIVL.routes.cart}.js`);
  const cart = await response.json();
  publish(EVENTS.cartUpdate, { cart });
  return cart;
};

/* ---------- Quantity input ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
    this.input?.addEventListener('change', () => this.dispatchEvent(new CustomEvent('quantity:change', { bubbles: true })));
  }
  onButtonClick(event) {
    event.preventDefault();
    const previous = Number(this.input.value);
    const step = Number(this.input.step) || 1;
    const min = Number(this.input.min) || 0;
    if (event.currentTarget.name === 'plus') this.input.value = previous + step;
    else this.input.value = Math.max(min, previous - step);
    if (previous !== Number(this.input.value)) this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

/* ---------- Details disclosure that closes on outside click / Escape ---------- */
class DetailsDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.details?.querySelector('summary');
    if (!this.details) return;
    this.details.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.details.addEventListener('focusout', (event) => {
      if (!this.details.contains(event.relatedTarget)) this.close();
    });
    document.addEventListener('click', (event) => {
      if (this.details.open && !this.details.contains(event.target)) this.close();
    });
  }
  close() {
    this.details.removeAttribute('open');
    this.summary?.setAttribute('aria-expanded', 'false');
  }
}
if (!customElements.get('details-disclosure')) customElements.define('details-disclosure', DetailsDisclosure);

/* ---------- Localization form ---------- */
class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="locale_code"], input[name="country_code"]');
    this.querySelectorAll('[data-value]').forEach((item) =>
      item.addEventListener('click', (event) => {
        event.preventDefault();
        this.input.value = item.dataset.value;
        this.form.submit();
      })
    );
  }
}
if (!customElements.get('localization-form')) customElements.define('localization-form', LocalizationForm);

/**
 * Product form: intercepts add-to-cart submissions (product page and quick add)
 * and pushes the new cart state to every listener.
 */
export const handleProductForm = async (event) => {
  const form = event.target;
  if (!form.matches('form[action$="/cart/add"], form[data-quick-add-form], form[data-product-form]')) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  button?.classList.add('is-loading');
  button?.setAttribute('aria-disabled', 'true');
  button?.querySelector('.loading-spinner')?.removeAttribute('hidden');

  const formData = new FormData(form);
  const sections = ['cart-drawer'];
  formData.append('sections', sections.join(','));
  formData.append('sections_url', window.location.pathname);

  try {
    const response = await fetch(window.FIVL.routes.cartAdd, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok || data.status) throw data;
    const cart = await refreshCart();
    publish(EVENTS.cartUpdate, { cart, sections: data.sections, source: 'product-form', item: data });
    if (window.FIVL.cartType !== 'drawer' && !form.hasAttribute('data-stay')) {
      window.location.href = window.FIVL.routes.cart;
    }
  } catch (error) {
    const message = error?.description || error?.message || window.FIVL.strings.cartError;
    let status = form.querySelector('[data-form-status]');
    if (!status) {
      status = document.createElement('p');
      status.setAttribute('data-form-status', '');
      status.setAttribute('role', 'alert');
      form.append(status);
    }
    status.className = 'form-status form-status--error';
    status.textContent = message;
  } finally {
    button?.classList.remove('is-loading');
    button?.removeAttribute('aria-disabled');
    button?.querySelector('.loading-spinner')?.setAttribute('hidden', '');
  }
};
document.addEventListener('submit', handleProductForm);

/* ---------- Quick add modal (products with options) ---------- */
document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-quick-add-url]');
  if (!trigger) return;
  const modal = document.querySelector('#QuickAddModal');
  if (!modal) return;
  event.preventDefault();
  const content = modal.querySelector('[data-quick-add-content]');
  content.innerHTML = '<div class="quick-add__loading"><span class="loading-spinner"></span></div>';
  modal.open(trigger);
  try {
    const url = new URL(trigger.dataset.quickAddUrl, window.location.origin);
    url.searchParams.set('section_id', 'quick-add');
    const response = await fetch(url.toString());
    const text = await response.text();
    const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-quick-add-section]');
    content.innerHTML = fresh ? fresh.outerHTML : '';
    // Scripts inside injected HTML never execute; import the modules explicitly instead.
    await Promise.all(
      Array.from(content.querySelectorAll('script[type="module"][src]')).map((script) => import(script.src).catch(() => null))
    );
    const focusTarget = content.querySelector('input:not([type="hidden"]), button, a');
    focusTarget?.focus();
  } catch (error) {
    content.innerHTML = `<p class="form-status form-status--error">${window.FIVL.strings.cartError}</p>`;
  }
});
subscribe(EVENTS.cartUpdate, ({ source }) => {
  if (source !== 'product-form') return;
  const modal = document.querySelector('#QuickAddModal');
  if (modal?.dialog?.open) modal.close();
});

window.FIVL = Object.assign(window.FIVL || {}, { subscribe, publish, EVENTS, formatMoney, cartRequest, refreshCart, trapFocus, removeTrapFocus });
